import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  CopyrightIcon,
  FileEditIcon,
  MessagesSquareIcon,
  UserCheckIcon,
  ZapIcon,
} from "lucide-react";
import type { LinkProps } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { todayFilter } from "@/lib/utils/date";
import { tokenStatsQueryOptions } from "@/lib/queries/tokenStats";
import { Skeleton } from "@/components/ui/skeleton";
import { userInfoQueryOptions } from "@/lib/queries/userInfo";

export const Route = createFileRoute("/_app/")({
  component: App,
  loader: ({ context: { queryClient } }) =>
    queryClient.prefetchQuery(tokenStatsQueryOptions(todayFilter())),
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "Track your daily token usage and quickly access key features in your personalized dashboard. Stay in control of your AI tools with real-time insights.",
      },
    ],
  }),
});

const quickActions = [
  {
    title: "Detect AI Generated Text",
    subtitle: "AI Detector",
    icon: <ZapIcon />,
    to: "/ai-detector",
  },
  // {
  //   title: "Highlight AI Generated Text",
  //   subtitle: "AI Highlighter",
  //   icon: <HighlighterIcon />,
  //   to: "/ai-highlighter",
  // },
  {
    title: "Detect Humanized AI Text",
    subtitle: "Humanized Text Detector",
    icon: <UserCheckIcon />,
    to: "/humanized-text-detector",
  },
  {
    title: "Detect Copyrighted Text",
    subtitle: "Copyright Detector",
    to: "/copyright-detector",
    icon: <CopyrightIcon />,
  },
  {
    title: "Analyze Your Writing",
    subtitle: "Writing Analysis",
    to: "/writing-analysis",
    icon: <FileEditIcon />,
  },
] as const satisfies Array<{
  title: string;
  subtitle: string;
  icon: ReactNode;
  to: LinkProps["to"];
}>;

function App() {
  const userInfo = useSuspenseQuery(userInfoQueryOptions);
  const tokenUsage = useQuery({
    ...tokenStatsQueryOptions(todayFilter()),
    select({ data }) {
      return Object.values(data).reduce((acc, v) => acc + v, 0);
    },
  });
  return (
    <div className="min-h-screen rounded-lg border p-5 text-gray-900 transition-colors duration-300 dark:bg-transparent dark:text-white">
      <h2 className="mb-1 text-xl font-semibold">
        Welcome back
        {userInfo.data.state === "logged_in"
          ? ", " + userInfo.data.info.full_name
          : null}
      </h2>
      <p className="mb-5 text-sm text-gray-600 dark:text-gray-400">
        Here's an overview of your Polygraf AI experience.
      </p>

      <div className="flex flex-wrap gap-5">
        <div className="min-w-[300px] flex-1">
          {/* Quick Actions Card */}
          <div className="mb-5 rounded-xl border bg-gray-50 p-5 shadow-sm dark:bg-transparent dark:shadow-md">
            <h3 className="mb-4 text-lg font-semibold">Quick Actions</h3>

            {quickActions.map((action) => (
              <Link
                key={action.title + action.to}
                to={action.to}
                className="mb-3 flex cursor-pointer items-center rounded-lg border bg-white p-3 hover:bg-gray-100 dark:bg-gray-800/30 dark:hover:bg-gray-700/30"
              >
                <div className="mr-3 flex size-8 items-center justify-center rounded-full text-pink-400">
                  {action.icon}
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{action.title}</span>
                  <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {action.subtitle}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Token Usage Card */}
          {userInfo.data.state === "logged_in" && (
            <div className="rounded-xl border bg-gray-50 p-5 shadow-sm dark:bg-transparent dark:shadow-md">
              <div className="flex items-center">
                <div className="mr-3 flex size-8 items-center justify-center rounded-full text-amber-400">
                  <MessagesSquareIcon />
                </div>
                <span className="text-sm">Tokens used today</span>
              </div>

              <div className="mt-2 mb-1 text-3xl font-bold">
                {tokenUsage.isSuccess ? (
                  tokenUsage.data
                ) : (
                  <Skeleton className="h-(--text-3xl) w-40 rounded-md bg-gray-600" />
                )}
              </div>

              {/* <div className="flex justify-end">
              <span className="inline-flex items-center rounded border bg-white px-2 py-1 text-xs text-green-600 dark:bg-green-900/20 dark:text-green-400">
                <ArrowUpRightIcon />
                10%
              </span>
            </div> */}
            </div>
          )}
        </div>

        {/* <div className="min-w-[300px] flex-1 rounded-xl border bg-gray-50 p-5 shadow-sm dark:bg-gray-800 dark:shadow-md">
          <div className="" data-slot="history-card">
            <h3 className="mb-4 text-lg font-semibold">Activities History</h3>

            <div className="mb-3 flex cursor-pointer items-center justify-between rounded-lg border bg-white p-3 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600">
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  Meeting with Davis County
                </span>
                <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Secure LLM Chat
                </span>
              </div>
              <ArrowRightIcon />
            </div>

            <div className="mb-3 flex cursor-pointer items-center justify-between rounded-lg border bg-white p-3 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600">
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  Elon Musk Salute Article
                </span>
                <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Stealth AI
                </span>
              </div>
              <ArrowRightIcon />
            </div>

            <div className="mb-3 flex cursor-pointer items-center justify-between rounded-lg border bg-white p-3 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600">
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  Elon Musk Salute Article
                </span>
                <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Humanized Text Detector
                </span>
              </div>
              <ArrowRightIcon />
            </div>

            <div className="mb-3 flex cursor-pointer items-center justify-between rounded-lg border bg-white p-3 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600">
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  Elon Musk Salute Article
                </span>
                <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Stealth AI
                </span>
              </div>
              <ArrowRightIcon />
            </div>

            <div className="mb-3 flex cursor-pointer items-center justify-between rounded-lg border bg-white p-3 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600">
              <div className="flex flex-col">
                <span className="text-sm font-medium">
                  Elon Musk Salute Article
                </span>
                <span className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Humanized Text Detector
                </span>
              </div>
              <ArrowRightIcon />
            </div>

            <div className="mt-4 flex justify-end">
              <button className="flex items-center rounded-lg border bg-white px-4 py-2 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600">
                <Table2Icon />
                History
              </button>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
}
